import { Controller } from "@hotwired/stimulus";

/**
 * There are two methods to create a modal: a turbo-frame and a partial. The
 * former uses the #modal turbo frame in the application layout to load/remove
 * content; and, the latter uses the "shared/modal" partial to show/hide
 * content.
 *
 * Why two methods? The partial method came first; it's used in many places;
 * and, it works great when the modal's content doesn't need unique ids. The
 * turbo-frame method was added later, and it works great when you do.
 */
export default class extends Controller {
  static targets = ["modal", "autofocus"];
  static values = { triggerParam: String };

  connect() {
    // Check if the modal was opened via URL param (rendered with flex instead of hidden)
    if (this.hasModalTarget && this.modalTarget.classList.contains("flex")) {
      this.wasOpen = true;
    }
  }

  disconnect() {
    // When the modal element is removed (e.g., by a turbo stream update),
    // ensure the body class is cleaned up if this modal was open.
    if (this.wasOpen) {
      document.body.classList.remove("modal-open");
    }
  }

  open() {
    // If the modal is using the turbo frame method, there's nothing to do; the
    // turbo frame loads the view, which looks ope). Otherwise, we need to show
    // the modal, which is hidden.
    if (!this.isTurboFrame()) {
      this.modalTarget.classList.remove("hidden");
      this.modalTarget.classList.add("flex");
    }

    this.wasOpen = true;
    this.modalTarget.closest("body").classList.add("modal-open");

    if (this.hasAutofocusTarget) {
      this.autofocusTarget.focus();
    }

    this.dispatch("open");
  }

  close() {
    this.wasOpen = false;
    this.modalTarget.closest("body").classList.remove("modal-open");

    // If the modal is using the turbo frame method, remove it; otherwise, hide it.
    if (this.isTurboFrame()) {
      this.modalTarget.remove();
    } else {
      this.modalTarget.classList.add("hidden");
      this.modalTarget.classList.remove("flex");
    }

    // If the modal was triggered by a URL param, remove it.
    if (this.triggerParamValue) {
      try {
        if (window.history.replaceState) {
          const url = new URL(window.location);
          url.searchParams.delete(this.triggerParamValue);
          window.history.replaceState({}, "", url);
        }
      } catch {}
    }
  }

  overlayClick(event) {
    if (event.target === this.modalTarget) {
      this.close();
    }
  }

  /**
   * Returns true if the modal is using the "turbo frame" method, as opposed
   * to the "partial" method.
   */
  isTurboFrame() {
    const parent = this.modalTarget.parentElement;

    return parent.tagName == "TURBO-FRAME" && parent.id == "modal";
  }
}
